type MarkGeometry = {
  cx: number;
  cy: number;
  radius: number;
  lineWidth: number;
  centerSpotRadius: number;
  contact: { cx: number; cy: number; radius: number };
  clearance: number;
};

const brandGeometry: MarkGeometry = {
  cx: 16,
  cy: 16,
  radius: 14,
  lineWidth: 4,
  centerSpotRadius: 4,
  contact: { cx: 28, cy: 26, radius: 4 },
  clearance: 1,
};

export const APP_ICON_BACKGROUND = "#eff1f1";
// The complete mark, including the lower-right spot, fits inside the maskable
// icon's central 40% radius. Keep the same proportions in every context.
export const APP_ICON_VIEW_BOX = "-9 -9 50 50";
export const BRAND_ICON_VERSION = "red-20261027";

function ringPath(mark: MarkGeometry) {
  const { cx, cy, radius, lineWidth, contact, clearance } = mark;
  const dx = contact.cx - cx;
  const dy = contact.cy - cy;
  const distance = Math.hypot(dx, dy);
  const contactAngle = Math.atan2(dy, dx);
  // Leave room for the round line caps without drawing a border on the spot.
  const separation = contact.radius + lineWidth / 2 + clearance;
  const cosine =
    (radius ** 2 + distance ** 2 - separation ** 2) / (2 * radius * distance);
  const gapAngle = Math.acos(Math.max(-1, Math.min(1, cosine)));
  const point = (angle: number) =>
    `${(cx + radius * Math.cos(angle)).toFixed(3)} ${(cy + radius * Math.sin(angle)).toFixed(3)}`;
  return `M${point(contactAngle + gapAngle)} A${radius} ${radius} 0 1 1 ${point(contactAngle - gapAngle)}`;
}

export function brandMarkElements(spotColor = "#ff7b14") {
  const { cx, cy, radius, lineWidth, centerSpotRadius, contact } =
    brandGeometry;
  return [
    <circle
      key="ball"
      cx={cx}
      cy={cy}
      r={radius + lineWidth / 2}
      fill="#ffffff"
    />,
    <path
      key="ring"
      d={ringPath(brandGeometry)}
      fill="none"
      stroke="#767b7d"
      strokeWidth={lineWidth}
      strokeLinecap="round"
    />,
    <circle
      key="center"
      cx={cx}
      cy={cy}
      r={centerSpotRadius}
      fill={spotColor}
    />,
    <circle
      key="contact"
      cx={contact.cx}
      cy={contact.cy}
      r={contact.radius}
      fill={spotColor}
    />,
  ];
}

// Return intrinsic elements: ImageResponse does not support component children
// inside an SVG. The on-page preview and installed app use this same artwork.
export function brandAppIconElements(rounded = false, spotColor = "#ff7b14") {
  return [
    <rect
      key="background"
      x="-9"
      y="-9"
      width="50"
      height="50"
      rx={rounded ? 12.5 : 0}
      fill={APP_ICON_BACKGROUND}
    />,
    ...brandMarkElements(spotColor),
  ];
}
